import React, { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { 
  Trash2, 
  UserPlus, 
  Settings, 
  X,
  Edit,
  CheckSquare,
  XCircle
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { enhancedSmartleadApi } from '@/lib/enhanced-smartlead-api'
import { useClients } from '@/hooks/useClients'
import { BulkEditDialog } from './BulkEditDialog'
import { DeactivationConfirmDialog } from './DeactivationConfirmDialog'
import { BulkOperationProgress } from './BulkOperationProgress'
import { useBulkOperationProgress } from '@/hooks/useBulkOperationProgress'
import { useOperationCleanup } from '@/hooks/useOperationCleanup'
import { CompactOperationIndicator } from './CompactOperationIndicator'

interface BulkActionToolbarProps {
  selectedCount: number
  selectedMailboxes: string[]
  totalMailboxes: number
  onUpdate: () => void
  onClearSelection: () => void
  onSelectAll: () => void
}

export function BulkActionToolbar({ 
  selectedCount, 
  selectedMailboxes, 
  totalMailboxes,
  onUpdate, 
  onClearSelection,
  onSelectAll
}: BulkActionToolbarProps) {
  const { data: clients = [] } = useClients()
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false)
  const [showDeactivationDialog, setShowDeactivationDialog] = useState(false)
  
  const {
    operationId,
    isProgressOpen,
    isLoading,
    startOperation,
    updateOperation,
    closeProgress,
    openProgress,
    onOperationComplete,
    checkActiveOperations
  } = useBulkOperationProgress()
  
  // Initialize operation cleanup
  useOperationCleanup()

  // Check for active operations on mount
  useEffect(() => {
    checkActiveOperations()
  }, [])

  const handleOpenProgressDialog = () => {
    if (operationId) {
      openProgress()
    }
  }


  const handleBulkClientAssignment = async (clientName: string) => {
    try {
      const opId = await startOperation(
        'client_assignment',
        selectedCount,
        { client_name: clientName },
        'User'
      )

      const { error } = await supabase
        .from('mailboxes')
        .update({ 
          client_name: clientName,
          updated_at: new Date().toISOString()
        })
        .in('id', selectedMailboxes)

      if (error) throw error

      await updateOperation(opId, {
        successful_count: selectedCount,
        failed_count: 0,
      })

      toast.success(`Assigned ${selectedCount} mailboxes to ${clientName}`)
      onUpdate()
      onClearSelection()
    } catch (error) {
      toast.error('Failed to assign client')
    }
  }

  const handleBulkProviderUpdate = async (provider: string) => {
    try {
      const opId = await startOperation(
        'provider_update',
        selectedCount,
        { provider },
        'User'
      )

      const { error } = await supabase
        .from('mailboxes')
        .update({ 
          provider: provider,
          updated_at: new Date().toISOString()
        })
        .in('id', selectedMailboxes)

      if (error) throw error

      await updateOperation(opId, {
        successful_count: selectedCount,
        failed_count: 0,
      })

      toast.success(`Updated ${selectedCount} mailboxes to ${provider}`)
      onUpdate()
      onClearSelection()
    } catch (error) {
      toast.error('Failed to update provider')
    }
  }

  const handleBulkWarmupToggle = async (enabled: boolean) => {
    try {
      // Start operation with descriptive name
      const operationName = enabled ? 'warmup_enable_toggle' : 'warmup_disable_toggle'
      const opId = await startOperation(
        operationName,
        selectedCount,
        { enabled },
        'User'
      )

      // Get mailboxes data to access smartlead_id
      const { data: mailboxes, error: fetchError } = await supabase
        .from('mailboxes')
        .select('id, email, smartlead_id')
        .in('id', selectedMailboxes)

      if (fetchError) throw fetchError

      let successCount = 0
      let failCount = 0
      const errors: string[] = []

      // Process each mailbox with rate limiting
      for (const mailbox of mailboxes) {
        try {
          if (!mailbox.smartlead_id) {
            errors.push(`${mailbox.email}: Mailbox is not linked to Smartlead`)
            failCount++
            continue
          }

          // Call Smartlead API to toggle warmup
          const result = await enhancedSmartleadApi.toggleWarmup(mailbox.smartlead_id, enabled)

          if (result.ok && result.message === "Email warmup details updated successfully!") {
            // Update local database if Smartlead API call was successful
            const dbUpdates: any = {
              warmup_status: enabled ? 'ACTIVE' : 'INACTIVE',
              updated_at: new Date().toISOString()
            }

            // Set warmup_reputation based on enabled state
            if (enabled) {
              dbUpdates.warmup_reputation = '100'
            } else {
              dbUpdates.warmup_reputation = '0%'
            }

            const { error: updateError } = await supabase
              .from('mailboxes')
              .update(dbUpdates)
              .eq('id', mailbox.id)

            if (updateError) {
              errors.push(`${mailbox.email}: Database update failed: ${updateError.message}`)
              failCount++
            } else {
              successCount++
            }
          } else {
            errors.push(`${mailbox.email}: Smartlead API call failed - ${result.message || 'Unknown error'}`)
            failCount++
          }
        } catch (error) {
          errors.push(`${mailbox.email}: ${error instanceof Error ? error.message : 'Unknown error'}`)
          failCount++
        }

        // Rate limiting - wait 1 second between API calls
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      // Update operation with results
      await updateOperation(opId, {
        successful_count: successCount,
        failed_count: failCount,
        errors: errors.length > 0 ? errors : undefined,
      })

      if (successCount > 0) {
        toast.success(`${enabled ? 'Enabled' : 'Disabled'} warmup for ${successCount} mailboxes`)
        onUpdate() // This will refresh mailbox health stats
      }
      
      if (failCount > 0) {
        toast.error(`Failed to toggle warmup for ${failCount} mailboxes. Check operation logs for details.`)
      }

      onClearSelection()
    } catch (error) {
      toast.error('Failed to toggle warmup')
    }
  }

  const handleBulkDeactivate = async (deactivationReason: string) => {
    try {
      // Get current user info for logging
      const { data: { user } } = await supabase.auth.getUser()
      const userIdentifier = user?.email || user?.id || 'Unknown User'

      // Start the operation and get operation ID
      const opId = await startOperation(
        'bulk_deactivate',
        selectedCount,
        { 
          mailbox_ids: selectedMailboxes,
          deactivation_reason: deactivationReason,
          user_email: user?.email,
          user_id: user?.id
        },
        userIdentifier
      )

      // Call edge function with operation ID for tracking
      const { data, error } = await supabase.functions.invoke('deactivate-mailbox-smartlead', {
        body: { 
          mailboxIds: selectedMailboxes,
          deactivationReason: deactivationReason,
          operationId: opId
        }
      })

      if (error) {
        throw new Error(`Edge function error: ${error.message}`)
      }

      // Show immediate feedback
      toast.success('Deactivation process started - track progress in the dialog')
      onClearSelection()

    } catch (error) {
      toast.error('Failed to start deactivation process')
    }
  }

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedCount} mailboxes? This action cannot be undone.`)) {
      return
    }

    try {
      const opId = await startOperation(
        'bulk_delete',
        selectedCount,
        {},
        'User'
      )

      const { error } = await supabase
        .from('mailboxes')
        .delete()
        .in('id', selectedMailboxes)

      if (error) throw error

      await updateOperation(opId, {
        successful_count: selectedCount,
        failed_count: 0,
      })

      toast.success(`Deleted ${selectedCount} mailboxes`)
      onUpdate()
      onClearSelection()
    } catch (error) {
      toast.error('Failed to delete mailboxes')
    }
  }

  return (
    <>
      <div className="w-full">
        {/* Integrated Selection Header */}
        <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg mb-4">
          <div className="flex items-center gap-3">
            <Badge variant="default" className="bg-primary text-primary-foreground">
              {selectedCount} selected
            </Badge>
            
            {selectedCount < totalMailboxes && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSelectAll}
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Select All ({totalMailboxes})
              </Button>
            )}
            
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkEditDialog(true)}
                disabled={isLoading}
              >
                <Edit className="w-4 h-4 mr-2" />
                Bulk Update
              </Button>

              <Select onValueChange={handleBulkClientAssignment}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Assign Client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.name}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select onValueChange={handleBulkProviderUpdate}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Mailbox Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Gmail">Gmail</SelectItem>
                  <SelectItem value="Outlook">Outlook</SelectItem>
                  <SelectItem value="Yahoo">Yahoo</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkWarmupToggle(true)}
                disabled={isLoading}
              >
                Enable Warmup
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkWarmupToggle(false)}
                disabled={isLoading}
              >
                Disable Warmup
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeactivationDialog(true)}
                disabled={isLoading}
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                <XCircle className="w-4 h-4 mr-2" />
                {isLoading && operationId ? 'Deactivating...' : 'Deactivate'}
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={isLoading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Operation Status Indicator */}
        {isLoading && operationId && (
          <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-800 text-sm">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Operation in progress... Check the progress dialog for details.</span>
          </div>
        )}

        {/* Quick Action Note */}
        <div className="text-sm text-muted-foreground text-center p-2 bg-muted/30 rounded border">
          💡 <strong>Quick Actions:</strong> Select mailboxes to assign clients, set providers, manage warmup, or use bulk update for advanced settings.
        </div>
      </div>
      
      <BulkEditDialog
        open={showBulkEditDialog}
        onOpenChange={setShowBulkEditDialog}
        selectedMailboxes={selectedMailboxes}
        selectedCount={selectedCount}
        onUpdate={onUpdate}
        onClearSelection={onClearSelection}
        providers={[]}
      />

      <DeactivationConfirmDialog
        open={showDeactivationDialog}
        onOpenChange={setShowDeactivationDialog}
        selectedCount={selectedCount}
        selectedMailboxes={selectedMailboxes}
        onConfirm={handleBulkDeactivate}
      />

      <BulkOperationProgress
        operationId={operationId}
        isOpen={isProgressOpen}
        onClose={closeProgress}
        onComplete={onOperationComplete}
      />

      {/* Compact Progress Indicator */}
      <CompactOperationIndicator
        operationId={operationId}
        onOpenDialog={handleOpenProgressDialog}
      />
    </>
  )
}